// LUMINANTE, pack par quantité (bougies et fondants).
//
// Un élément personnalisé, aucune bibliothèque, aucune requête externe.
// Le script n'est chargé que par le bloc, donc uniquement sur les fiches
// bougies : les autres familles ne téléchargent rien.
//
// Ce que fait ce fichier, et rien d'autre :
//   · afficher le bon nombre d'emplacements selon le palier choisi ;
//   · recalculer le total à partir des prix réels des options ;
//   · envoyer une ligne de panier par bougie choisie ;
//   · neutraliser le sélecteur de quantité natif tant que le pack est
//     actif, pour qu'un pack de 3 ne devienne jamais 9 articles.
//
// La remise n'est jamais appliquée ici : seule une réduction automatique
// Shopify agit sur le prix, au panier comme au paiement. Ce script se
// contente d'annoncer le même montant que celui qui sera retiré.
//
// Depuis le 06/09 les paliers sont des MONTANTS REMISÉS en centimes, plus
// des pourcentages : c'est la forme exacte de la réduction Shopify posée
// en face (montant fixe, appliquée une seule fois par commande). Il n'y a
// donc plus d'arrondi par ligne à reproduire, et le total annoncé est le
// total payé quelle que soit la combinaison de parfums.

if (!customElements.get('lum-bundle')) {
  class LumBundle extends HTMLElement {
    connectedCallback() {
      this.paliers = [...this.querySelectorAll('input[type="radio"]')];
      this.zone = this.querySelector('[data-selection]');
      this.champs = [...this.querySelectorAll('[data-emplacement]')];
      this.selects = [...this.querySelectorAll('select[data-parfum]')];
      this.total = this.querySelector('[data-total]');
      this.bouton = this.querySelector('[data-ajouter]');
      this.erreur = this.querySelector('[data-erreur]');
      if (!this.paliers.length || !this.bouton) return;

      /* Le total suit le FORMAT MONÉTAIRE DE LA BOUTIQUE (Paramètres →
         Devise), via l'utilitaire d'Horizon qui reproduit le filtre `money`
         de Liquid. Avant, il était formaté en dur en fr-FR ("79,99 €"),
         donc différent des prix des cartes rendus par Liquid ("€79,99").
         Le module est chargé une fois ; s'il manque, repli sur l'ancien
         formatage pour ne jamais afficher un total vide. */
      this.argent = import('@theme/money-formatting')
        .then((m) => m.formatMoney)
        .catch(() => null);
      this.paliers.forEach((r) => r.addEventListener('change', () => this.majPalier()));
      this.selects.forEach((s) => s.addEventListener('change', () => this.majTotal()));
      this.bouton.addEventListener('click', () => this.ajouter());

      this.majPalier();
    }

    get quantite() {
      const choisi = this.paliers.find((r) => r.checked);
      return choisi ? Number(choisi.value) : 1;
    }

    // Deux modes, distingués par la SEULE présence des menus de parfum :
    //   · avec menus  : une ligne de panier par article, chacun son parfum ;
    //   · sans menus  : le produit de la fiche, en N exemplaires.
    // Le second sert aux packs de volume (fondants 1/5/10/20), où vingt
    // menus obligatoires rendraient la fiche inutilisable.
    get sansChoix() {
      return this.selects.length === 0;
    }

    // Une seule fonction dit ce que vaut l'emplacement i, quel que soit le
    // mode : c'est ce qui évite deux logiques de prix et d'ajout.
    lignePour(i) {
      if (this.sansChoix) {
        const id = Number(this.dataset.variante || 0);
        return id ? { id, prix: Number(this.dataset.prix || 0) } : null;
      }
      const opt = this.selects[i]?.selectedOptions[0];
      if (!opt || !opt.value) return null;
      return { id: Number(opt.value), prix: Number(opt.dataset.prix || 0) };
    }

    // Une seule logique d'achat à l'écran : à un article, c'est le bloc
    // d'achat natif qui agit et le bouton du pack s'efface ; dès deux
    // articles, c'est l'inverse. Sans quoi un pack de 3 combiné à une
    // quantité de 3 mettrait neuf bougies au panier.
    majAchat() {
      const multiple = this.quantite > 1;
      const col = this.closest('.product-details') || document;

      const quantite = col.querySelector('.quantity-selector-wrapper, quantity-selector-component');
      const achatNatif = col.querySelector('.buy-buttons-block');
      [quantite, achatNatif].forEach((el) => el && el.toggleAttribute('hidden', multiple));

      // La quantité native revient toujours à 1 : aucune valeur fantôme
      // ne peut survivre à un aller-retour entre les paliers.
      const champ = quantite?.querySelector('input[name="quantity"]');
      if (champ && multiple) champ.value = 1;

      // Le bouton du pack ne sert qu'aux paliers multiples.
      this.bouton.toggleAttribute('hidden', !multiple);
      this.total.toggleAttribute('hidden', !multiple);
    }

    majPalier() {
      const n = this.quantite;
      this.champs.forEach((champ) => {
        const rang = Number(champ.dataset.emplacement);
        champ.toggleAttribute('hidden', rang > n);
        const select = champ.querySelector('select');
        if (select) select.disabled = rang > n;
      });
      // Sans menus de parfum la zone n'est pas rendue du tout : on ne la
      // pilote que si elle existe.
      this.zone?.toggleAttribute('hidden', n < 2);
      this.majAchat();
      this.majTotal();
    }

    // Le total vient toujours des prix réels portés par les options :
    // si un parfum change de prix dans Shopify, le calcul suit.
    majTotal() {
      const n = this.quantite;
      let somme = 0;
      let complet = true;
      for (let i = 0; i < n; i += 1) {
        const ligne = this.lignePour(i);
        if (!ligne) {
          complet = false;
          continue;
        }
        somme += ligne.prix;
      }
      if (!complet) {
        this.total.textContent = '';
        return;
      }

      // Le montant est retiré une seule fois du total, comme le fait la
      // réduction Shopify. Un montant qui dépasserait le total est ignoré
      // plutôt que d'afficher un prix négatif.
      const choisi = this.paliers.find((r) => r.checked);
      let montant = Number(choisi?.dataset.remiseMontant || 0);
      if (!Number.isFinite(montant) || montant < 0 || montant > somme) montant = 0;
      const apres = somme - montant;
      // Valeur des socles offerts (1 par bougie) : elle ne sert qu'au prix
      // de référence affiché, jamais au montant payé.
      const socle = Math.max(0, Number(this.dataset.socleValeur || 0)) * n;
      const reference = somme + socle;
      const devise = this.dataset.devise || 'EUR';
      const gabarit = this.dataset.moneyFormat || '';
      const repli = (v) => (v / 100).toLocaleString('fr-FR', { style: 'currency', currency: devise });
      (this.argent || Promise.resolve(null)).then((formatMoney) => {
        const formate = formatMoney && gabarit ? (v) => formatMoney(v, gabarit, devise) : repli;
        // Le total est composé d'ÉLÉMENTS, plus d'une chaîne : sans cela le
        // prix de référence ne peut pas être coloré à part (demandé par Antho
        // le 26/09, « prix barré en rouge »).
        //
        // ⚠️ Construit avec createElement + textContent, jamais avec innerHTML :
        // le montant vient du gabarit monétaire de la boutique, il n'a rien à
        // faire dans un parseur HTML.
        this.total.textContent = '';
        if (montant > 0 || socle > 0) {
          const paye = document.createElement('span');
          paye.className = 'lum-bundle__total-paye';
          paye.textContent = formate(apres);

          const ancien = document.createElement('span');
          ancien.className = 'lum-bundle__total-ancien';
          ancien.textContent = formate(reference);

          this.total.append(paye, document.createTextNode(' au lieu de '), ancien);
        } else {
          this.total.textContent = formate(somme);
        }
      });
    }

    message(texte) {
      if (!this.erreur) return;
      this.erreur.textContent = texte || '';
      this.erreur.toggleAttribute('hidden', !texte);
    }

    async ajouter() {
      const n = this.quantite;
      const lignes = [];
      if (this.sansChoix) {
        // Une seule ligne de quantité N plutôt que N lignes identiques :
        // Shopify les regrouperait de toute façon, autant envoyer la forme
        // que le panier affichera.
        const ligne = this.lignePour(0);
        if (!ligne) {
          this.message('Produit indisponible pour le moment.');
          return;
        }
        lignes.push({ id: ligne.id, quantity: n });
      } else {
        for (let i = 0; i < n; i += 1) {
          const select = this.selects[i];
          const opt = select?.selectedOptions[0];
          if (!opt || !opt.value) {
            this.message(`Choisissez un parfum pour l’emplacement ${i + 1}.`);
            select?.focus();
            return;
          }
          lignes.push({ id: Number(opt.value), quantity: 1 });
        }
      }
      this.message('');
      this.bouton.setAttribute('aria-busy', 'true');
      this.bouton.disabled = true;

      const racine = window.Shopify?.routes?.root || '/';

      try {
        // Les sections du panier sont demandées dans la même requête :
        // Shopify renvoie leur HTML à jour, ce qui évite un second appel.
        const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((el) => el.dataset.sectionId)
          .filter(Boolean);

        const reponse = await fetch(`${racine}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(sections.length ? { items: lignes, sections: sections.join(',') } : { items: lignes }),
        });
        const data = await reponse.json();
        if (!reponse.ok) throw new Error(data.description || data.message || 'Ajout impossible.');

        // On laisse le thème reprendre la main plutôt que de piloter le
        // tiroir nous-mêmes : Horizon écoute l'événement standard
        // shopify:cart:lines-update pour rafraîchir le tiroir, la pastille
        // du panier et les lignes. On émet donc le même événement, avec la
        // vraie classe du thème quand elle est disponible.
        let emis = false;
        try {
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const differe = CartLinesUpdateEvent.createPromise?.();
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: lignes.map((l) => ({ merchandiseId: `gid://shopify/ProductVariant/${l.id}`, quantity: l.quantity })),
              ...(differe ? { promise: differe.promise } : {}),
            })
          );
          if (differe) {
            /* ⚠️ NE PAS résoudre avec la réponse de /cart/add.js. Elle ne
               contient que les lignes ajoutées : ni `cart`, ni `item_count`.
               Or cart-icon.js et header-actions.js lisent `cart.totalQuantity`
               et retombaient donc sur 0, ce qui VIDAIT la pastille du panier
               alors que l'ajout venait de réussir (panier réel à 2, pastille
               masquée et remise à 0, mesuré à 375 comme à 1 440 px).

               On relit donc le vrai panier et on résout comme le fait
               product-form.js du thème : c'est le mécanisme natif, pas une
               seconde logique de panier, et la source de vérité reste le
               panier Shopify. `sections` est repassé dans `detail`, là où
               component-cart-items.js va le chercher pour rafraîchir les
               lignes du tiroir sans second appel. */
            try {
              const panier = await (
                await fetch(`${racine}cart.js`, {
                  headers: { Accept: 'application/json' },
                  credentials: 'same-origin',
                })
              ).json();

              differe.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse
                  ? CartLinesUpdateEvent.createCartFromAjaxResponse(panier)
                  : { totalQuantity: panier.item_count },
                detail: {
                  items: panier.items,
                  itemCount: panier.item_count,
                  sections: data.sections,
                  source: 'lum-bundle',
                },
              });
            } catch (err) {
              if (differe.reject) differe.reject(err);
            }
          }
          emis = true;
        } catch (_) {
          // Module indisponible : on retombe sur la page panier, qui reste
          // une destination correcte plutôt qu'un ajout invisible.
        }
        if (!emis) window.location.href = `${racine}cart`;
      } catch (e) {
        this.message(e.message || 'Ajout impossible pour le moment.');
      } finally {
        this.bouton.removeAttribute('aria-busy');
        this.bouton.disabled = false;
      }
    }
  }

  customElements.define('lum-bundle', LumBundle);
}
